/* src/components/settings/sections/GameplayAudioSettings.js */
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog } from '@fortawesome/free-solid-svg-icons';
import {
    SectionCard,
    ToggleSwitch,
    DropdownInput,
    LoadingSpinner
} from '../SettingSectionComponents';

const GameplayAudioSettings = ({
    localInstantFeedback,
    onInstantFeedbackChange, // This will be the setLocalInstantFeedback function
    localSelectedVoiceId,
    onSelectedVoiceIdChange, // This will be the (e) => setLocalSelectedVoiceId(e.target.value) handler
    voiceOptions,
    isGeneralSectionDisabled,
    audioReady,
    onResumeAudio,
    sampleLetters,
    onTestLetter,
    activeButton,
    isSavingAppSettings,
    appSaveStatus,
    onSaveGameplayAudio
}) => {
    return (
        <SectionCard title="Gameplay & Audio" icon={faCog}>
            <div className="space-y-3 divide-y divide-gray-200">
                {/* Instant Feedback */}
                <ToggleSwitch
                    label="Instant Feedback"
                    description="Show visual/audio cues immediately after a match response or miss."
                    checked={localInstantFeedback}
                    onChange={onInstantFeedbackChange} // Directly pass the setter
                    disabled={isGeneralSectionDisabled}
                />
                {/* Audio Voice Selection */}
                <DropdownInput
                    label="Voice & Language"
                    id="audioVoice"
                    value={localSelectedVoiceId}
                    onChange={onSelectedVoiceIdChange} // Directly pass the handler
                    options={voiceOptions}
                    disabled={isGeneralSectionDisabled || voiceOptions.length === 0}
                    description={voiceOptions.length === 0 ? "No voices available" : "Select the voice for letter sounds."}
                    className="pt-3"
                />
                {/* Inline Audio Test */}
                <div className="pt-3">
                    <div className="flex items-center justify-between mb-3">
                        <span className="flex-grow flex flex-col mr-4">
                            <span className="text-sm font-medium text-gray-700">Audio Output Test</span>
                            <span className="text-xs text-gray-500 mt-0.5">Check letter sounds for the selected voice.</span>
                        </span>
                        {!audioReady && (
                            <button
                                className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-sm disabled:opacity-50"
                                onClick={onResumeAudio}
                                disabled={isGeneralSectionDisabled}
                            >
                                Activate Audio
                            </button>
                        )}
                    </div>

                    {!audioReady && (
                        <p className="text-xs text-yellow-700 mb-2">Click "Activate Audio" first if sounds don't play.</p>
                    )}

                    {/* Grid for testing letters */}
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                        {sampleLetters.map(letter => (
                            <button
                                key={letter}
                                className={`px-4 py-2 rounded text-sm transition-colors ${
                                    activeButton === `letter-${letter}`
                                        ? 'bg-blue-300 text-blue-900' // Highlight when active
                                        : 'bg-blue-100 hover:bg-blue-200 text-blue-800'
                                } ${isGeneralSectionDisabled || !audioReady ? 'opacity-50 cursor-not-allowed' : ''}`}
                                onClick={() => onTestLetter(letter)}
                                disabled={isGeneralSectionDisabled || !audioReady}
                            >
                                {letter}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            {/* Save Button Area for Gameplay & Audio */}
            <div className="mt-5 pt-4 border-t border-gray-200 flex items-center justify-end space-x-3">
                {appSaveStatus.section === 'gameplay' && appSaveStatus.message && (
                    <span className={`text-sm font-medium ${appSaveStatus.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                        {appSaveStatus.message}
                    </span>
                )}
                <button
                    onClick={onSaveGameplayAudio}
                    disabled={isGeneralSectionDisabled}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center justify-center min-w-[200px] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                    {isSavingAppSettings && appSaveStatus.section === 'gameplay' ? (
                        <><LoadingSpinner /> Saving...</>
                    ) : (
                        'Save Gameplay & Audio'
                    )}
                </button>
            </div>
        </SectionCard>
    );
};

export default GameplayAudioSettings;